// components/advertiser-wallet/AdvertiserWallet.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarClock, Gift, Star, Wallet as WalletIcon } from "lucide-react";
import { toast } from "sonner";

import {
  ModalType,
  ViewType,
  WalletBallance,
  TopRewardSpend,
  WalletTransaction,
  StatisticsCardProps,
  SpendTrendPoint,
  SpendTrendInsight,
  RewardStatusFilter,
  RewardOption,
} from "./types";
import WalletView from "./wallet-view";
import ReportView from "./report-view";
import Modals from "./modals";
import useRequest from "../../../components/hooks/use-req";
import { formatCurrency } from "../../../utils/function";
import { REWARD_SORT_OPTIONS } from "./constants";
import { buildPeriodRequestParams, DateRange, EMPTY_DATE_RANGE, PeriodValue } from "../../../components/period-filter";

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

const AdvertiserWallet: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewType>("wallet");
  const [modal, setModal] = useState<ModalType>(null);
  const [addedAmount, setAddedAmount] = useState<number>(0);
  const [isAddingFunds, setIsAddingFunds] = useState(false);
  const [lowBalanceThreshold, setLowBalanceThreshold] = useState(100);
  const [quickFund, setQuickFund] = useState<number>(10000);
  const [customFundInput, setCustomFundInput] = useState<string>("10,000");
  const [thresholdInput, setThresholdInput] = useState("5,000");
  const [emailNotif, setEmailNotif] = useState(true);
  const [whatsappNotif, setWhatsappNotif] = useState(true);
  const [isSavingThreshold, setIsSavingThreshold] = useState(false);
  const authToken = localStorage.getItem("authToken");

  const [advWallet, setAdvWallet] = useState<WalletBallance | null>(null);
  const [topRewards, setTopRewards] = useState<TopRewardSpend[]>([]);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsTotalPages, setTransactionsTotalPages] = useState(1);
  const [transactionsPageSize, setTransactionsPageSize] = useState(10);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [statistics, setStatistics] = useState<StatisticsCardProps | null>(null);

  // Detailed Reward Trends: chart + metrics period selector
  const [periodDays, setPeriodDays] = useState(7);
  const [spendTrend, setSpendTrend] = useState<SpendTrendPoint[]>([]);
  const [spendInsight, setSpendInsight] = useState<SpendTrendInsight | null>(null);

  // Reward Spend table filters/sort/pagination
  const [rewardOptions, setRewardOptions] = useState<RewardOption[]>([]);
  const [rewardStatusFilter, setRewardStatusFilter] = useState<RewardStatusFilter>("active");
  const [rewardIdFilter, setRewardIdFilter] = useState<string>("");
  const [rewardPeriodFilter, setRewardPeriodFilter] = useState<PeriodValue>("daily");
  const [rewardCustomRange, setRewardCustomRange] = useState<DateRange>(EMPTY_DATE_RANGE);
  const [rewardSortValue, setRewardSortValue] = useState<string>("spend_desc");
  const [rewardSpendItems, setRewardSpendItems] = useState<TopRewardSpend[]>([]);
  const [rewardSpendPage, setRewardSpendPage] = useState(1);
  const [rewardSpendTotalPages, setRewardSpendTotalPages] = useState(1);
  const [rewardSpendPageSize] = useState(5);
  const [rewardSpendLoading, setRewardSpendLoading] = useState(false);

  const isLowBalance =
    (advWallet?.recharged_wallet_balance ?? 0) <= lowBalanceThreshold;
  const quickFundOptions = [10000, 50000, 100000];
  const MIN_TOPUP = 10000;
  const MAX_TOPUP = 1000000;

  const { makeRequest: addFunds } = useRequest("/dashboard/advertiser/wallet/add-funds", "POST", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: updateLowBalanceAlert } = useRequest("/dashboard/advertiser/wallet/low-balance-alert", "PATCH", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: getWalletBalance } = useRequest("/dashboard/advertiser/wallet/overview", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: getTopRewards } = useRequest("/dashboard/advertiser/wallet/top-reward-spends", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: getTransactions } = useRequest("/dashboard/advertiser/wallet/transactions", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: getTrendChart } = useRequest("/dashboard/advertiser/wallet/spend-trends", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: getMetrics } = useRequest("/dashboard/advertiser/wallet/stats", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: getAllRewardPrograms } = useRequest("/woocommerce/rewards", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: getAdvertiserProfile } = useRequest("/auth/auth/me-advertiser", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  const formatNaira = (n: number) => `₦${n?.toLocaleString()}`;

  const closeModal = () => setModal(null);

  useEffect(() => {
    if ((location.state as { openAddFunds?: boolean } | null)?.openAddFunds) {
      setModal("addFundsAmount");
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the spend-trend chart + the stats cards together, keyed on the
  // "Detailed Reward Trends" period selector so both stay in sync.
  const loadTrendAndStats = useCallback(async (days: number) => {
    const [trendResponse] = await getTrendChart(undefined, { period_days: days });
    setSpendTrend(trendResponse?.data || []);
    setSpendInsight(trendResponse?.insight || null);

    const [statsResponse] = await getMetrics(undefined, { period_days: days });
    setStatistics(statsResponse || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTrendAndStats(periodDays);
  }, [periodDays, loadTrendAndStats]);

  useEffect(() => {
    const refreshWalletBalnce = async () => {
      const [response] = await getWalletBalance();
      const userData = response;

      setAdvWallet(userData);

    };
    refreshWalletBalnce();
  }, []);

  // Seed the "Set Low Balance Alert" modal's threshold from the advertiser's saved value
  // (response has no `data` wrapper — fields are top-level, same as elsewhere this endpoint is used).
  useEffect(() => {
    const fetchLowBalanceThreshold = async () => {
      const [response] = await getAdvertiserProfile();
      const threshold = response?.low_balance_threshold;
      if (threshold !== undefined && threshold !== null) {
        setLowBalanceThreshold(threshold);
        setThresholdInput(threshold.toLocaleString());
      }
    };
    fetchLowBalanceThreshold();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unfiltered top rewards used by the Wallet tab's summary card
  useEffect(() => {
    const fetchTopRewards = async () => {
      const [response] = await getTopRewards();
      setTopRewards(response?.data || []);
    };
    fetchTopRewards();
  }, []);

  // Reward options for the "specific reward" filter dropdown
  useEffect(() => {
    const fetchRewardOptions = async () => {
      const [response] = await getAllRewardPrograms();
      const rewards = Array.isArray(response?.rewards)
        ? response.rewards
        : Array.isArray(response)
        ? response
        : [];
      setRewardOptions(rewards.map((r: any) => ({ id: r.id, title: r.title })));
    };
    fetchRewardOptions();
  }, []);

  // Filtered/sorted/paginated Reward Spend table (Report view)
  const loadRewardSpend = useCallback(
    async (page = 1) => {
      const periodParams = buildPeriodRequestParams(rewardPeriodFilter, rewardCustomRange);
      if (!periodParams) return;

      setRewardSpendLoading(true);
      try {
        const sortOption =
          REWARD_SORT_OPTIONS.find((o) => o.value === rewardSortValue) || REWARD_SORT_OPTIONS[0];

        const params: Record<string, any> = {
          status_filter: rewardStatusFilter,
          ...periodParams,
          sort_by: sortOption.sortBy,
          sort_order: sortOption.sortOrder,
          page,
          page_size: rewardSpendPageSize,
        };
        if (rewardIdFilter) params.reward_id = rewardIdFilter;

        const [response] = await getTopRewards(undefined, params);
        setRewardSpendItems(response?.data || []);
        setRewardSpendPage(response?.page || page);
        setRewardSpendTotalPages(response?.total_pages || 1);
      } finally {
        setRewardSpendLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rewardStatusFilter, rewardIdFilter, rewardPeriodFilter, rewardCustomRange, rewardSortValue, rewardSpendPageSize]
  );

  useEffect(() => {
    loadRewardSpend(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardStatusFilter, rewardIdFilter, rewardPeriodFilter, rewardCustomRange, rewardSortValue]);

  const handleRewardSpendPageChange = (page: number) => {
    loadRewardSpend(page);
  };

  const loadTransactions = useCallback(
    async (page = 1) => {
      setTransactionsLoading(true);
      try {
        const [response] = await getTransactions(undefined, {
          page,
          page_size: transactionsPageSize,
        });

        setTransactions(response?.data || []);
        setTransactionsPage(response?.page || page);
        setTransactionsTotalPages(response?.total_pages || 1);
        setTransactionsPageSize(response?.page_size || transactionsPageSize);
      } finally {
        setTransactionsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transactionsPageSize]
  );

  useEffect(() => {
    loadTransactions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTransactionsPageChange = (page: number) => {
    loadTransactions(page);
  };

  const handleQuickFundSelect = (amount: number) => {
    setQuickFund(amount);
    setCustomFundInput(amount.toLocaleString());
  };

  const handleCustomFundChange = (raw: string) => {
    const digitsOnly = raw.replace(/[^\d]/g, "");
    setCustomFundInput(digitsOnly ? Number(digitsOnly).toLocaleString() : "");
    setQuickFund(Number(digitsOnly) || 0);
  };

  // 7.5% VAT is added on top of the entered funding amount — the backend still just gets a
  // single `amount`, but it's the VAT-inclusive total, not the raw funding amount typed in.
  // Left un-rounded on purpose — e.g. 100 * 7.5% must stay 7.5, not become 8.
  const VAT_RATE = 0.075;
  const vatAmount = quickFund * VAT_RATE;
  const totalToPay = quickFund + vatAmount;

  const handleConfirmAddFunds = async () => {
    const amount = totalToPay;
    if (!quickFund) return;
    if (quickFund < MIN_TOPUP) {
      toast.error(`Minimum top-up amount is ${formatNaira(MIN_TOPUP)}`);
      return;
    }
    if (quickFund > MAX_TOPUP) {
      toast.error(`Maximum top-up amount is ${formatNaira(MAX_TOPUP)}`);
      return;
    }

    setIsAddingFunds(true);
    try {
      // The backend's own default callback URL is used server-side — don't override it here.
      const [response, error] = await addFunds({ amount });

      const paymentUrl =
        response?.payment_link ||
        error?.payment_link ||
        response?.data?.payment_link ||
        error?.data?.payment_link ||
        response?.data?.authorization_url ||
        response?.authorization_url ||
        response?.data?.payment_url;

      if (paymentUrl) {
        // Flutterwave's redirect back to /wallet/subscription doesn't carry the amount
        // (and the verify endpoint hasn't been returning it either), so stash what we
        // already know here, keyed by tx_ref, for that page to pick back up. Stash the
        // funding amount the advertiser actually entered (no VAT) — that's what should be
        // shown on return, not the VAT-inclusive total that was charged.
        const txRef = response?.tx_ref || error?.tx_ref || response?.data?.tx_ref;
        if (txRef) {
          localStorage.setItem("pendingWalletTopup", JSON.stringify({ tx_ref: txRef, amount: quickFund }));
        }

        window.location.href = paymentUrl;
        return;
      }

      if (error || !response?.status) {
        toast.error(error?.message || response?.message || "Failed to add funds");
        return;
      }

      setAdvWallet((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          recharged_wallet_balance: prev.recharged_wallet_balance + amount,
        };
      });
      setAddedAmount(amount);
      setModal("addFundsSuccess");
    } catch (err) {
      toast.error("An unexpected error occurred");
    } finally {
      setIsAddingFunds(false);
    }
  };

  const handleSaveThreshold = async () => {
    const numeric = Number(thresholdInput.replace(/[^\d]/g, "")) || 0;

    setIsSavingThreshold(true);
    try {
      const [response, error] = await updateLowBalanceAlert({
        alert_threshold: numeric,
        email_notifications_enabled: emailNotif,
      });

      if (error || response?.status === false) {
        toast.error(error?.message || response?.message || "Failed to update low balance alert");
        return;
      }

      setLowBalanceThreshold(numeric);
      toast.success(response?.message || "Low balance alert updated");
      closeModal();
    } finally {
      setIsSavingThreshold(false);
    }
  };

  const runwayDays = 3;

  const walletStats = useMemo(
    () => [
      {
        icon: <WalletIcon size={16} />,
        label: "Available Balance",
        value: formatNaira(advWallet?.recharged_wallet_balance || 0),
        helper: "Add funds to keep programs running",
        valueClassName: isLowBalance ? "text-orange-500" : "",
      },
      {
        icon: <Star size={16} />,
        label: "Active Programs",
        value: advWallet?.active_programs.toString() || "0",
        helper: "Programs using wallet funds",
      },
      {
        icon: <Gift size={16} />,
        label: "Rewards Paid",
        value: formatNaira(advWallet?.rewards_paid || 0),
        helper: "Total rewards paid",
      },
      {
        icon: <CalendarClock size={16} />,
        label: "Estimated Runway",
        value: `${advWallet?.estimated_runway_days || 0} days`,
        helper: "Based on last 7 days spend avg.",
      },
    ],
    [advWallet, isLowBalance]
  );

  const reportStats = [
    { icon: <Gift size={16} />, label: "Rewards Paid", value: `${formatCurrency(statistics?.rewards_paid || 0, 'NGN', 'symbol')}`, helper: "Total rewards paid to recommenders." },
    { icon: <Star size={16} />, label: "Cancelled Orders", value: statistics?.cancelled_orders || 0, helper: "Orders cancelled after a rewarded referral." },
    { icon: <WalletIcon size={16} />, label: "Refund Initiated", value: `${formatCurrency(statistics?.refund_initiated || 0, 'NGN', 'code')}`, helper: "Referred orders that were cancelled." },
    { icon: <WalletIcon size={16} />, label: "Revenue", value: `${formatCurrency(statistics?.revenue || 0, 'NGN', 'symbol')}`, helper: "Total revenue generated by programs" },
  ];

  const spendTrendChartData = useMemo(
    () => spendTrend.map((point) => ({ day: point.day_label, value: point.amount })),
    [spendTrend]
  );

  return (
    <div className="mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {/* Header */}
      <div className="mb-8">
        {view === "report" && (
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <button onClick={() => setView("wallet")} className="hover:text-black">
              Wallet
            </button>
            <span>›</span>
            <span className="text-black font-medium">Full Report</span>
          </div>
        )}
        <h1 className="text-2xl lg:text-3xl flex gap-3 font-semibold text-black">Wallet</h1>
        <p className="text-[#717171] text-base sm:text-[18px]">
          Monitor your wallet balance, funding activity, and reward
          <br className="hidden sm:block" /> program spend.
        </p>
      </div>

      {view === "wallet" ? (
        <WalletView
          balance={advWallet?.recharged_wallet_balance || 0}
          isLowBalance={isLowBalance}
          lowBalanceThreshold={lowBalanceThreshold}
          quickFund={quickFund}
          quickFundOptions={quickFundOptions}
          formatNaira={formatNaira}
          runwayDays={runwayDays}
          walletStats={walletStats}
          topRewards={topRewards}
          onSetLowBalance={() => setModal("lowBalance")}
          onQuickFundSelect={handleQuickFundSelect}
          onAddFunds={() => setModal("addFundsAmount")}
          onViewReport={() => setView("report")}
        />
      ) : (
        <ReportView
          reportStats={reportStats}
          spendTrendData={spendTrendChartData}
          insightMessage={spendInsight?.message}
          periodDays={periodDays}
          onPeriodDaysChange={setPeriodDays}
          rewardSpend={rewardSpendItems}
          rewardOptions={rewardOptions}
          rewardStatusFilter={rewardStatusFilter}
          onRewardStatusFilterChange={setRewardStatusFilter}
          rewardIdFilter={rewardIdFilter}
          onRewardIdFilterChange={setRewardIdFilter}
          rewardPeriodFilter={rewardPeriodFilter}
          onRewardPeriodFilterChange={setRewardPeriodFilter}
          rewardCustomRange={rewardCustomRange}
          onRewardCustomRangeChange={setRewardCustomRange}
          rewardSortValue={rewardSortValue}
          onRewardSortValueChange={setRewardSortValue}
          rewardSpendPage={rewardSpendPage}
          rewardSpendTotalPages={rewardSpendTotalPages}
          rewardSpendLoading={rewardSpendLoading}
          onRewardSpendPageChange={handleRewardSpendPageChange}
          transactions={transactions}
          runwayDays={runwayDays}
          formatNaira={formatNaira}
          transactionsPage={transactionsPage}
          transactionsTotalPages={transactionsTotalPages}
          transactionsLoading={transactionsLoading}
          onTransactionsPageChange={handleTransactionsPageChange}
        />
      )}

      {/* Modals */}
      <Modals
        modal={modal}
        thresholdInput={thresholdInput}
        emailNotif={emailNotif}
        whatsappNotif={whatsappNotif}
        customFundInput={customFundInput}
        quickFund={quickFund}
        quickFundOptions={quickFundOptions}
        minTopup={MIN_TOPUP}
        maxTopup={MAX_TOPUP}
        vatAmount={vatAmount}
        totalToPay={totalToPay}
        addedAmount={addedAmount}
        isAddingFunds={isAddingFunds}
        isSavingThreshold={isSavingThreshold}
        formatNaira={formatNaira}
        onClose={closeModal}
        onThresholdChange={setThresholdInput}
        onEmailNotifChange={setEmailNotif}
        onWhatsappNotifChange={setWhatsappNotif}
        onSaveThreshold={handleSaveThreshold}
        onCustomFundChange={handleCustomFundChange}
        onQuickFundSelect={handleQuickFundSelect}
        onConfirmAddFunds={handleConfirmAddFunds}
      />
    </div>
  );
};

export default AdvertiserWallet;
